import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '../i18n';

/**
 * The public marketing surface: every route that is prerendered at build time,
 * listed in the sitemap and served in both languages.
 *
 * This is the single source of truth. The build script walks it to decide what
 * to render, the sitemap generator walks it to decide what to publish, and the
 * browser entry point walks it to decide whether a url belongs to the light
 * marketing bundle or to the full application.
 *
 * Authentication and in-app routes are deliberately absent — they are noindexed
 * and have nothing to gain from being rendered ahead of time.
 */
export type MarketingRouteKey =
  | 'home'
  | 'pricing'
  | 'about'
  | 'customers'
  | 'careers'
  | 'blog'
  | 'blogPost'
  | 'press'
  | 'contact'
  | 'notFound';

export type MarketingRoute = {
  key: MarketingRouteKey;
  /** React Router path pattern, relative to the language prefix. */
  path: string;
  /** Sitemap hints. Omitted for routes that should not be listed. */
  sitemap?: { changefreq: string; priority: string };
};

export const MARKETING_ROUTES: MarketingRoute[] = [
  { key: 'home', path: '/', sitemap: { changefreq: 'weekly', priority: '1.0' } },
  { key: 'pricing', path: '/pricing', sitemap: { changefreq: 'weekly', priority: '0.9' } },
  { key: 'about', path: '/about', sitemap: { changefreq: 'monthly', priority: '0.8' } },
  { key: 'customers', path: '/customers', sitemap: { changefreq: 'monthly', priority: '0.8' } },
  { key: 'blog', path: '/blog', sitemap: { changefreq: 'weekly', priority: '0.7' } },
  { key: 'blogPost', path: '/blog/:slug', sitemap: { changefreq: 'monthly', priority: '0.6' } },
  { key: 'careers', path: '/careers', sitemap: { changefreq: 'weekly', priority: '0.7' } },
  { key: 'press', path: '/press', sitemap: { changefreq: 'monthly', priority: '0.6' } },
  { key: 'contact', path: '/contact', sitemap: { changefreq: 'monthly', priority: '0.6' } },
  // Rendered for unknown urls so a broken link gets a real "not found" page
  // instead of a silent redirect that reads as a soft 404.
  { key: 'notFound', path: '*' },
];

export type MarketingMatch = {
  route: MarketingRoute;
  params: Record<string, string>;
};

/**
 * Resolves a language-stripped path (`/blog/attribution-windows-explained`) to
 * the route that serves it. Falls back to the not-found route, so every url
 * reaching the marketing bundle renders something.
 */
export function matchMarketingRoute(path: string): MarketingMatch {
  const normalized = path === '/' ? '/' : path.replace(/\/+$/, '');

  for (const route of MARKETING_ROUTES) {
    if (route.path === '*') continue;

    if (!route.path.includes(':')) {
      if (route.path === normalized) return { route, params: {} };
      continue;
    }

    const patternSegments = route.path.split('/');
    const pathSegments = normalized.split('/');
    if (patternSegments.length !== pathSegments.length) continue;

    const params: Record<string, string> = {};
    const matched = patternSegments.every((segment, index) => {
      if (segment.startsWith(':')) {
        params[segment.slice(1)] = decodeURIComponent(pathSegments[index]);
        return Boolean(pathSegments[index]);
      }
      return segment === pathSegments[index];
    });

    if (matched) return { route, params };
  }

  return { route: MARKETING_ROUTES[MARKETING_ROUTES.length - 1], params: {} };
}

/** True when this url is served by the marketing bundle rather than the app. */
export function isMarketingPath(path: string): boolean {
  return matchMarketingRoute(path).route.key !== 'notFound';
}

/** Languages a public page is published in, default language first. */
export const PUBLISHED_LANGUAGES: SupportedLanguage[] = [...SUPPORTED_LANGUAGES];
